import React, { useState, useEffect, useRef } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { Task } from '../../../../types/task';
import { tasksAtom } from '../../../../atoms/v0/taskAtoms';
import { projectsAtom } from '../../../../atoms/v0/projectAtoms';
import {
  focusedTaskIdAtom,
  playingTaskIdsAtom,
  headerActiveDisplayAtom,
  setFocusedTaskAtom,
} from '../../../../atoms/v3/focusAtoms';
import { useTranslation } from 'react-i18next';

interface FocusHeaderProps {
  // onTaskCardClick is removed as primary interaction is swipe
  // and internal clicks will manage headerActiveDisplayAtom directly.
}

/**
 * 专注页面的头部组件
 * 显示当前日期和时间，或者当前专注的任务卡片
 */
export const FocusHeader: React.FC<FocusHeaderProps> = () => {
  const { t } = useTranslation(['v3/focus']);
  const [currentTime, setCurrentTime] = useState(new Date());
  const tasks = useAtomValue(tasksAtom);
  const projects = useAtomValue(projectsAtom);
  const [headerDisplay, setHeaderDisplay] = useAtom(headerActiveDisplayAtom);
  const playingTaskIds = useAtomValue(playingTaskIdsAtom);
  const [, globallySetFocusedTask] = useAtom(setFocusedTaskAtom); // Renamed to avoid conflict
  const [, setGlobalFocusedTaskId] = useAtom(focusedTaskIdAtom); // For direct setting from header

  const sliderRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false); // To control select-none
  const [swipeAnimDirection, setSwipeAnimDirection] = useState<'next' | 'prev'>('next');

  const playingTasks = playingTaskIds
    .map(id => tasks.find(task => task.id === id))
    .filter((task): task is Task => !!task && !task.done);

  // Combines clock and valid playing tasks for the swiper
  const validPlayingTaskIds = playingTasks.map(task => task.id);
  const swiperItems: ('clock' | string)[] = ['clock', ...validPlayingTaskIds];

  const currentTask = headerDisplay !== 'clock' ? tasks.find(task => task.id === headerDisplay && !task.done) : null;
  const taskProject = currentTask?.projectId
    ? projects.find(project => project.id === currentTask.projectId)
    : null;

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSwipeTo = (itemId: 'clock' | string, direction: 'next' | 'prev') => {
    setSwipeAnimDirection(direction);
    setHeaderDisplay(itemId);
    if (itemId !== 'clock') {
      setGlobalFocusedTaskId(itemId);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (swiperItems.length <= 1) return;
    // e.preventDefault(); // Prevent default only if actual drag starts, or text selection on tap is fine
    // Forcing preventDefault to take control for swipe
    e.preventDefault();
    touchStartXRef.current = e.touches[0].clientX;
    setIsSwiping(true); // Indicate potential swipe start for select-none
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping || !touchStartXRef.current) return;

    // Prevent page scroll during swipe on the header
    // This is already handled by e.preventDefault() in onTouchStart if we always prevent.
    // If onTouchStart's preventDefault is conditional, this one becomes more important.
    // For now, assuming onTouchStart always prevents, this one is redundant but harmless.
    e.preventDefault();

    // Optional: Could add logic here for live feedback during swipe if desired
    // (e.g., moving the card slightly with the finger before releasing)
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // If preventDefault was used in onTouchStart, it might be good to also use it here
    // to ensure no lingering default actions are triggered on touch end, though often not necessary.
    // e.preventDefault();

    if (touchStartXRef.current === null || swiperItems.length <= 1) {
      setIsSwiping(false);
      return;
    }

    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchEndX - touchStartXRef.current;
    const currentIndex = swiperItems.indexOf(headerDisplay);

    if (Math.abs(diffX) > 50) { // Threshold for swipe
      if (diffX > 0) { // Swipe Right (Previous item)
        if (currentIndex > 0) {
          handleSwipeTo(swiperItems[currentIndex - 1], 'prev');
        }
      } else { // Swipe Left (Next item)
        if (currentIndex < swiperItems.length - 1) {
          handleSwipeTo(swiperItems[currentIndex + 1], 'next');
        }
      }
    }
    touchStartXRef.current = null;
    setIsSwiping(false);
  };

  const formattedDate = format(currentTime, 'yyyy年MM月dd日');
  const formattedTime = format(currentTime, 'HH:mm:ss');
  const formattedWeekday = format(currentTime, 'EEEE');

  const formatDate = (date: string | null) => {
    if (!date) return null;
    return format(new Date(date), 'MM月dd日 HH:mm');
  };

  // Determine the visual slide index for the indicator dots
  const visualSlideIndex = swiperItems.indexOf(headerDisplay);

  return (
    <div className="px-4 pt-4 pb-6 font-ios">
      <div
        className={`relative touch-pan-y ${
          isSwiping ? 'select-none' : '' // Disable text selection during swipe
        }`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={sliderRef}
      >
        <AnimatePresence mode="wait" initial={false}>
          {headerDisplay !== 'clock' && currentTask ? (
            <motion.div
              key={`task-card-${currentTask.id}`}
              initial={{ opacity: 0, x: swipeAnimDirection === 'next' ? 100 : -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: swipeAnimDirection === 'prev' ? 100 : -100 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg p-5 shadow-ios dark:shadow-ios-dark border border-ios-gray5/30 dark:border-ios-dark-gray4/30"
            >
              <div className="flex items-center mb-3">
                <div className={`w-2.5 h-2.5 rounded-full mr-2 ${
                  currentTask.vital && currentTask.urgent
                    ? 'bg-ios-red dark:bg-ios-dark-red'
                    : currentTask.vital
                      ? 'bg-ios-orange dark:bg-ios-dark-orange'
                      : currentTask.urgent
                        ? 'bg-ios-blue dark:bg-ios-dark-blue'
                        : 'bg-ios-green dark:bg-ios-dark-green'
                }`}></div>
                <div className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                  {currentTask.vital && currentTask.urgent
                    ? '重要且紧急'
                    : currentTask.vital
                      ? '重要'
                      : currentTask.urgent
                        ? '紧急'
                        : '进行中'}
                </div>
                {currentTask.startAt && (
                  <div className="ml-auto text-ios-footnote text-ios-gray dark:text-ios-dark-gray flex items-center">
                    <svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {formatDate(currentTask.startAt)}
                    {currentTask.cost && ` (${Math.floor(currentTask.cost / 60)}分钟)`}
                  </div>
                )}
              </div>
              <div className="text-ios-title1 font-semibold mb-2 text-ios-black dark:text-white leading-tight">
                {currentTask.title}
              </div>
              {taskProject && (
                <div className="flex items-center text-ios-subheadline text-ios-blue dark:text-ios-dark-blue mb-2">
                  <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 9H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {taskProject.title}
                </div>
              )}
              {currentTask.notes && (
                <div className="mt-3 pt-3 border-t border-ios-gray5/30 dark:border-ios-dark-gray4/30">
                  <div className="text-ios-body text-ios-gray dark:text-ios-dark-gray">
                    {currentTask.notes}
                  </div>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="clock"
              initial={{ opacity: 0, x: swipeAnimDirection === 'next' ? 100 : -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: swipeAnimDirection === 'prev' ? 100 : -100 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="flex flex-col items-center w-full"
              onClick={() => {
                if (playingTasks.length > 0) {
                  handleSwipeTo(playingTasks[0].id, 'next');
                }
              }}
            >
              <div className="text-[3.5rem] leading-none font-light text-ios-black dark:text-white text-center tracking-tight">
                {formattedTime}
              </div>
              <div className="text-ios-title3 text-ios-gray dark:text-ios-dark-gray text-center mt-2 flex items-center">
                <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {formattedDate} <span className="mx-1.5">·</span> {formattedWeekday}
              </div>
              <div className="text-ios-footnote text-ios-gray2 dark:text-ios-dark-gray2 text-center mt-4">
                {playingTasks.length > 0
                  ? t('v3/focus:tips.swipeOrClickToView', '向左或点击可查看任务')
                  : t('v3/focus:tips.clickPlayToStart', '点击任务右侧的播放按钮开始专注')
                }
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {swiperItems.length > 1 && (
          <div className="flex justify-center mt-6 space-x-2">
            {swiperItems.map((item, index) => (
              <div
                key={`page-indicator-${item}-${index}`}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  visualSlideIndex === index
                    ? 'bg-ios-blue dark:bg-ios-dark-blue w-6 h-2'
                    : 'bg-ios-gray3 dark:bg-ios-dark-gray3 w-2 h-2'
                }`}
                onClick={() => handleSwipeTo(item, visualSlideIndex < index ? 'next' : 'prev')}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to determine swipe direction for animations (optional, can be managed with a state)
// let swipeDirection: 'next' | 'prev' = 'next'; // Removed this unused variable
// This is a simplified way; for more robust direction detection, you might need a state
// variable that is set before headerDisplay changes, or pass direction to AnimatePresence custom prop.
// For framer-motion key-based re-evaluation, this global variable approach is tricky.
// Consider using a state for swipeDirection if animations are not smooth.
